/**
 * Permission/Role Hooks
 */
import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { permissionService } from '../services/permissionService';
import { queryTags } from '../queryTags';
import { getQueryClient } from '../queryClient';
import type {
  GetRolesParams,
  CreateRolePayload,
  UpdateRolePayload,
} from '../types/permission.types';
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';

/**
 * Get roles list hook
 */
export function useGetRoles(
  params?: GetRolesParams,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [
      queryTags.permission,
      params?.page || 1,
      params?.limit || 10,
      params?.search || '',
    ],
    queryFn: () => permissionService.getRoles(params),
    ...options,
  });
}

/**
 * Get single role by ID hook
 */
export function useGetRole(
  id: string,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.permission, id],
    queryFn: () => permissionService.getRole(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Create role hook
 */
export function useCreateRole(
  options?: UseMutationOptions<any, any, CreateRolePayload, any>
) {
  return useApiMutation<any, any, CreateRolePayload, any>({
    mutationFn: ((data: CreateRolePayload) => permissionService.createRole(data)) as any,
    onSuccess: (data, variables, context, mutation) => {
      // Always invalidate queries to refresh the list
      getQueryClient().invalidateQueries({ queryKey: [queryTags.permission] });
      // Call custom onSuccess if provided
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context, mutation);
      }
    },
    onError: options?.onError,
  });
}

/**
 * Update role hook
 */
export function useUpdateRole(
  options?: UseMutationOptions<any, any, { id: string; data: UpdateRolePayload }, any>
) {
  return useApiMutation<any, any, { id: string; data: UpdateRolePayload }, any>({
    mutationFn: ((variables: { id: string; data: UpdateRolePayload }) =>
      permissionService.updateRole(variables.id, variables.data)) as any,
    onSuccess: (data, variables, context, mutation) => {
      // Always invalidate queries to refresh the list
      getQueryClient().invalidateQueries({ queryKey: [queryTags.permission] });
      // Call custom onSuccess if provided
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context, mutation);
      }
    },
    onError: options?.onError,
  });
}

/**
 * Delete role hook
 */
export function useDeleteRole(
  options?: UseMutationOptions<any, any, string, any>
) {
  return useApiMutation<any, any, string, any>({
    mutationFn: ((id: string) => permissionService.deleteRole(id)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.permission] });
    },
    ...options,
  });
}

